// useVagasListagem.js
import { useLatestAsyncRequest } from '@spark/ui-components/hooks/useLatestAsyncRequest'
import { useState, useEffect, useCallback } from 'react'
import { getVagas } from '@spark/services/recrutamento-rh'

// mapeia chaves do FilterPanel (UI) para as chaves da API
function mapUiToApi(ui = {}) {
  const out = { ativo: 1 };

  // UI: Titulo  -> API: titulo
  if (ui.Titulo && ui.Titulo.trim()) out.titulo = ui.Titulo.trim();

  // UI: Data_abertura (um campo) -> API: dataAberturaInicio/Fim (mesmo dia)
  if (ui.Data_abertura && ui.Data_abertura.trim()) {
    out.dataAberturaInicio = ui.Data_abertura;
    out.dataAberturaFim = ui.Data_abertura;
  }

  // UI: NecessitaCNH ('', '0', '1') -> API: necessitaCNH (0/1)
  if (ui.NecessitaCNH !== '' && ui.NecessitaCNH !== undefined) {
    out.necessitaCNH = Number(ui.NecessitaCNH);
  }

  // se tiver outros campos (ex.: descricao, search), mapeie aqui
  if (ui.Descricao && ui.Descricao.trim()) out.descricao = ui.Descricao.trim();
  if (ui.search && ui.search.trim()) out.search = ui.search.trim();

  return out;
}

export const useVagasListagem = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterLoading, setFilterLoading] = useState(false)
  const [totalRegistros, setTotalRegistros] = useState(0)
  const [paginaAtual, setPaginaAtual] = useState(1)
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(20)
  const [filtrosUI, setFiltrosUI] = useState({ // chaves vindas do FilterPanel
    Titulo: '',
    Data_abertura: '',
    NecessitaCNH: '',
  })

  const { beginRequest, isCurrentRequest, isMounted } = useLatestAsyncRequest()

  const fetchData = useCallback(async (pagina = paginaAtual, limite = quantidadePorPagina, ui = filtrosUI) => {
    if (!isMounted()) return

    const requestId = beginRequest()
    try {
      setLoading(true)

      const apiFilters = mapUiToApi(ui)
      const res = await getVagas({ page: pagina, limit: limite, ...apiFilters })

      if (!isCurrentRequest(requestId)) return
      const registros = res?.registros ?? []
      const total = res?.totalRegistros ?? 0

      const dados = registros.map(v => ({
        id: v.id ?? v.Id,
        Titulo: v.Titulo,
        Endereco: v.Endereco,
        Descricao: v.Descricao,
        DescricaoVaga: v.DescricaoVaga,
        Salario: v.Salario,
        Ativo: v.Ativo,
        Data_abertura: v.Data_abertura,
        NecessitaCNH: v.NecessitaCNH,
        beneficios: v.beneficios,
        complementares: v.complementares,
      }))

      setData(dados)
      setTotalRegistros(Number(total) || 0)
    } catch (e) {
      if (!isCurrentRequest(requestId)) return
      setData([])
      setTotalRegistros(0)
    } finally {
      if (isCurrentRequest(requestId)) {
        setLoading(false)
        setFilterLoading(false)
      }
    }
  }, [beginRequest, filtrosUI, isCurrentRequest, isMounted, paginaAtual, quantidadePorPagina])

  useEffect(() => { fetchData(paginaAtual, quantidadePorPagina, filtrosUI) }, [fetchData])

  // aplica filtros vindos do FilterPanel (com chaves da UI)
  const handleFilterApply = useCallback((newUiFilters) => {
    setFiltrosUI(newUiFilters)
    setPaginaAtual(1)
    setFilterLoading(true)
    fetchData(1, quantidadePorPagina, newUiFilters)
  }, [fetchData, quantidadePorPagina])

  // limpar filtros para estado padrão
  const handleLimparFiltros = useCallback(() => {
    const cleared = { Titulo: '', Data_abertura: '', NecessitaCNH: '' }
    setFiltrosUI(cleared)
    setPaginaAtual(1)
    setFilterLoading(true)
    fetchData(1, quantidadePorPagina, cleared)
  }, [fetchData, quantidadePorPagina])

  return {
    data,
    loading,
    filterLoading,
    totalRegistros,
    paginaAtual,
    quantidadePorPagina,
    filtrosUI,
    handleFilterApply,
    handleLimparFiltros,
    setPaginaAtual,
    setQuantidadePorPagina,
    refetch: () => fetchData(paginaAtual, quantidadePorPagina, filtrosUI),
  }
}
